import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Snackbar, 
  Alert, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Checkbox,
  FormControl,
  InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import SurveyProgressBar from '../components/survey/SurveyProgressBar';
import AIQuestionGenerator from '../components/survey/AIQuestionGenerator';
import QuestionList from '../components/survey/QuestionList';
import SurveyPreviewPanel from '../components/survey/SurveyPreviewPanel';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { SurveyQuestion, CreationMethod } from '../types/survey';
import { createSurveyFromForm } from '../services/surveyService';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '2fr 1fr'
  }
}));

const LeftColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3)
}));

const AISurveyBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const campaignData = location.state?.campaignData;
  const campaignId = location.state?.campaignId;

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | null>(null);
  const [editForm, setEditForm] = useState<{
    question: string;
    answerType: string;
    isMandatory: boolean;
  }>({
    question: '',
    answerType: 'text',
    isMandatory: false
  });

  const handleGenerateQuestions = async (productUrl: string) => {
    setIsGenerating(true);
    setError(null);
    console.log('Generating questions for:', productUrl);

    try {
      const url = `${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.SURVEY.GENERATE_QUESTIONS}`;
      
      const response = await api.post<{
        success: boolean;
        data?: {
          questions: Array<{
            question_text: string;
            suggested_answer_type: string;
          }>;
          total_generated?: number;
        };
        message?: string;
        error?: string;
      }>(url, {
        product_url: productUrl
      });

      console.log('AI Question Generation Response:', response);

      if (response.success && response.data?.questions) {
        // Map backend answer types to frontend answer types
        const mapAnswerType = (suggestedType: string): string => {
          const typeMap: Record<string, string> = {
            'text': 'text',
            'rating': 'rating',
            'number': 'number',
            'yes_no': 'yesno',
            'yesno': 'yesno',
            'multiple_choice': 'multiple_choice',
            'multiple choice': 'multiple_choice',
          };
          
          const normalizedType = suggestedType.toLowerCase().trim();
          return typeMap[normalizedType] || 'text'; // Default to 'text' if unknown
        };

        // Convert API response to UI format
        const generatedQuestions: SurveyQuestion[] = response.data.questions.map((q, index) => ({
          id: `question-${Date.now()}-${index + 1}`,
          question: q.question_text,
          answerType: mapAnswerType(q.suggested_answer_type),
          isMandatory: false, // All questions are optional by default
        }));

        setQuestions(generatedQuestions);
        setSuccess(`Successfully generated ${response.data.total_generated || generatedQuestions.length} questions!`);
      } else {
        throw new Error(response.message || response.error || 'Failed to generate questions');
      }
    } catch (err) {
      console.error('Error generating questions:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to generate questions. Please try again.';
      setError(errorMessage);
      setQuestions([]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEditQuestion = (question: SurveyQuestion) => {
    setEditingQuestion(question);
    setEditForm({
      question: question.question || '',
      answerType: question.answerType || 'text',
      isMandatory: question.isMandatory || false
    });
  };

  const handleSaveEdit = () => {
    if (!editingQuestion || !editForm.question.trim()) {
      return;
    }

    const updatedQuestions = questions.map(q => 
      q.id === editingQuestion.id
        ? {
            ...q,
            question: editForm.question.trim(),
            answerType: editForm.answerType,
            isMandatory: editForm.isMandatory
          }
        : q
    );

    setQuestions(updatedQuestions);
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const handleReorderQuestions = (reorderedQuestions: SurveyQuestion[]) => {
    setQuestions(reorderedQuestions);
  };

  const handleSaveAndContinue = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Validate campaign ID
      if (!campaignId) {
        throw new Error('Campaign ID is missing. Please go back and create a campaign first.');
      }

      if (questions.length === 0) {
        throw new Error('Please generate at least one question for the survey');
      }

      // Convert UI questions to API format
      const apiQuestions = questions.map((q, index) => {
        const converted = {
          question_text: q.question || '',
          answer_type: q.answerType || 'text',
          is_mandatory: Boolean(q.isMandatory),
          question_order: index + 1, // Order based on position in array (1-indexed)
          ...(q.options && q.options.length > 0 ? { options: q.options } : {})
        };
        console.log(`Question ${index + 1}: isMandatory=${q.isMandatory}, is_mandatory=${converted.is_mandatory}`);
        return converted;
      });

      console.log('AI Survey - API Questions:', JSON.stringify(apiQuestions, null, 2));

      // Create survey via API
      const response = await createSurveyFromForm({
        campaignId: campaignId,
        surveyName: campaignData?.campaign_name || 'AI Generated Survey',
        description: 'Survey generated using AI',
        creationMethod: CreationMethod.AI,
        questions: apiQuestions as any,
      });

      console.log('AI Survey created successfully:', response);
      
      const surveyData = {
        surveyId: response.data?.survey_id,
        questions: response.data?.questions || questions,
        method: 'ai' as const
      };

      setSuccess('Survey created successfully! Redirecting...');
      
      // Navigate to customer upload step
      navigate('/campaigns/create/customers', {
        state: { campaignData, surveyData, campaignId }
      });

    } catch (err) {
      console.error('AI Survey creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create survey. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Left Column - Main Content */}
          <LeftColumn>
            {/* Page Heading */}
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                Create New Survey
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                Add questions manually or use AI to generate them from a product URL.
              </Typography>
            </Box>

            {/* Progress Bar */}
            <SurveyProgressBar
              currentStep={2}
              totalSteps={3}
              stepLabel="Add Questions"
            />

            {/* AI Question Generator */}
            <AIQuestionGenerator
              onGenerateQuestions={handleGenerateQuestions}
              isGenerating={isGenerating}
            />

            {/* Question List */}
            {questions.length > 0 && (
              <QuestionList
                questions={questions}
                onEditQuestion={handleEditQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                onReorderQuestions={handleReorderQuestions}
              />
            )}
          </LeftColumn>

          {/* Right Column - Survey Preview */}
          <Box>
            <SurveyPreviewPanel
              questions={questions}
              onSaveAndContinue={handleSaveAndContinue}
              disabled={questions.length === 0 || isLoading}
            />
          </Box>
        </ContentArea>
      </MainContent>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Edit Question Dialog */}
      <Dialog 
        open={!!editingQuestion} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Question Text"
              fullWidth
              multiline
              rows={3}
              value={editForm.question}
              onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
              placeholder="Enter your question here..."
            />

            <FormControl fullWidth>
              <InputLabel>Answer Type</InputLabel>
              <Select
                value={editForm.answerType}
                label="Answer Type"
                onChange={(e) => setEditForm({ ...editForm, answerType: e.target.value })}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="rating">Rating (1-5)</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="yesno">Yes/No</MenuItem>
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.isMandatory}
                  onChange={(e) => setEditForm({ ...editForm, isMandatory: e.target.checked })}
                />
              }
              label="Make this question mandatory"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelEdit}
            sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#F3F4F6'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={!editForm.question.trim()}
            sx={{
              backgroundColor: '#4F46E5',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#4338CA',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AISurveyBuilder;

